import { HTMLInputProps, InputGroupProps, InputGroup } from '@blueprintjs/core'
import React from 'react'
import CX from 'classnames'

import './FormattedPriceInput.scss'

// We need to extend both of those. But writing
//    interface FormattedPriceInputProps extends InputGroupProps & HTMLInputProps
// upsets typescript, because "value" doesn't have the same type.
// Something about this bitwise operator gives one of them precedence.
// This ensures you can pass props like id
type SomeProps = InputGroupProps & HTMLInputProps

export interface FormattedPriceInputProps extends SomeProps {
  min?: number
  max?: number
  onValueChange: (valueAsString: string) => void
  // onChange is not supported, please use onValueChange
  onChange?: null
}

// A NumericInput that formats numbers so they look prices
// (like "8.00" and "3.50", instead of "8" and "3.5")
// This component will format numbers onBlur. But calling code should ensure the initial value is formatted nicely.
export const FormattedPriceInput: React.FC<FormattedPriceInputProps> = props => {
  const { onValueChange, ...otherProps } = props
  const min = props.min || 0.01

  const onBlur = event => {
    if (props.onBlur) {
      props.onBlur(event)
    }

    // Format number as "8.00" instead of "8.0" so it looks like currency

    // Clamp min and max, since this component doesn't support clampValueOnBlur
    // (don't change "" to "0.01" on blur)
    if (props.value?.length > 0) {
      let numericValue = Number(props.value)
      if (numericValue < min) {
        numericValue = min
      }
      if (numericValue > props.max) {
        numericValue = props.max
      }

      const valueAsString = numericValue.toFixed(2)
      onValueChange(valueAsString)
    }
  }

  // We really want to use NumericInput here. But they have a bug: sometimes you can pass "3.50" as the current prop,
  // but it will have "3.5" in its internal state, and it will show that instead :-(
  // Since we don't use the stepper buttons or keyboard shortcuts, we'll follow the blueprint team's advice
  // (https://github.com/palantir/blueprint/issues/4096) and use InputGroup with type="number"
  return (
    <InputGroup
      onChange={e => onValueChange(e.currentTarget.value)}
      {...otherProps}
      onFocus={event => {
        // Select the entire contents when this input is focused. Basically re-implement selectAllOnFocus from NumericInput
        event.target.select()

        if (props.onFocus) {
          props.onFocus(event)
        }
      }}
      className={CX('numeric-input-hide-arrows', props.className)}
      type="number" // let the native input filter out non-numeric inputs
      onBlur={onBlur}
    />
  )
}
